import React, { useState } from "react";
import TextTitle from "../common/ui/typography/TextTitle";
import Button from "../common/ui/buttons/Button";
import { useInfiniteQuery } from "react-query";
import { statistics } from "../../api/statistics";
import { useGlobalStore } from "../../store";
import "./style.scss";
import { TableColumn } from "../common/Table/TableTypes";
import moment from "moment";
import Table from "../common/Table/Table";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { ForceUpdateItem } from "../../api/statistics/types";
import NavigationDrawer from "../common/ui/navigationDrawer/NavigationDrawer";
import DrawerView from "../common/ui/navigationDrawer/DrawerView";
import ForceUpdateDriversAndVehiclesList from "./ForceUpdateDriversAndVehiclesList";

export const ForceUpdate = () => {
  const selectedCompany = useGlobalStore(state => state.selectedCompany);
  const [isDrawerOpen, setIsDrawerOpen] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    ["force-update-list", selectedCompany],
    ({ pageParam = 1 }) =>
      statistics.getUsersByEld({
        eldType: selectedCompany,
        page: pageParam,
        perPage: 10,
      }),
    {
      getNextPageParam: (_lastPage, allPages) => {
        const total = _lastPage?.data?.count ?? 0;
        const perPage = 10;
        const loadedItems = allPages.length * perPage;

        return loadedItems < total ? allPages.length + 1 : undefined;
      },

      enabled: !!selectedCompany,
    },
  );

  const allItems: ForceUpdateItem[] =
    data?.pages?.flatMap((page: any) =>
      page.data.map((item: any) => ({
        id: item.id,
        androidVersion: item.androidVersion,
        iosVersion: item.iosVersion,
        createdBy: item.createdBy,
        createdAt: moment(item.createdAt).format("MMM D, YYYY hh:mm A"),
        companiesCount: item.companiesCount,
      })),
    ) ?? [];

  const onClickRow = (item: ForceUpdateItem) => {
    setIsDrawerOpen(item.id);
  };

  const columns: TableColumn<ForceUpdateItem>[] = [
    { header: "Android", accessor: "androidVersion", clickRow: onClickRow },
    { header: "iOS", accessor: "iosVersion", clickRow: onClickRow },
    { header: "Companies", accessor: "companiesCount", clickRow: onClickRow },
    { header: "By", accessor: "createdBy", clickRow: onClickRow },
    { header: "Date", accessor: "createdAt", clickRow: onClickRow },
  ];

  const lastRowRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  });

  return (
    <div>
      <div className={"force-update__header d-flex align-items-center"}>
        <TextTitle level={"large"} className='d-flex align-center gap-2'>
          Force Update
        </TextTitle>
        <div style={{ marginLeft: "auto" }} className={"d-flex gap-2"}>
          <Button
            main
            to={"/force-update/create"}
            beforeIcon={"plus"}
            beforeClass='force-update__button'
          >
            <span className='force-update__button--text'>Add new update</span>
          </Button>
          <Button
            beforeIcon={"arrow-clockwise"}
            onClick={() => refetch()}
            beforeClass='force-update__button'
          >
            <span className='force-update__button--text'>Refresh</span>
          </Button>
        </div>
      </div>

      <div className='force-update__table mt-4'>
        <Table
          columns={columns}
          data={allItems}
          isLoading={isLoading}
          renderRow={(item, column, rowIndex, colIndex) => {
            const value = item[column.accessor as keyof ForceUpdateItem];
            const isLastRow = rowIndex === allItems.length - 1;
            return <div ref={isLastRow ? lastRowRef : undefined}>{value}</div>;
          }}
        />
      </div>

      <NavigationDrawer
        modelValue={!!isDrawerOpen || !!companyId}
        onUpdateModelValue={() =>
          companyId ? setCompanyId(0) : setIsDrawerOpen("")
        }
        width='1248px'
      >
        <DrawerView
          closeDrawer={() =>
            companyId ? setCompanyId(0) : setIsDrawerOpen("")
          }
          header={({ closeDrawer }) => (
            <div className={"d-flex align-items-center w-100"}>
              <TextTitle level={"1"} className='d-flex align-center gap-2'>
                Force Update
              </TextTitle>
              <div style={{ marginLeft: "auto" }}>
                <Button
                  size={"xs"}
                  onClick={closeDrawer}
                  beforeIcon={"dismiss"}
                  beforeClass='mr-0'
                />
              </div>
            </div>
          )}
        >
          {isDrawerOpen && (
            <ForceUpdateDriversAndVehiclesList
              id={isDrawerOpen}
              setCompanyId={setCompanyId}
              companyId={companyId}
            />
          )}
        </DrawerView>
      </NavigationDrawer>
    </div>
  );
};
